import { ReactNode, useEffect, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconArrow, IconGrip, IconSparkle, IconTrash } from './icons';
import {
  Section,
  SlideSpan,
  SlideWidth,
  autoAdjustSpans,
  spanToClass,
  toSpan,
} from './layout';
import type { EditorMode } from './layout';

/**
 * Rendu d'une section dans un slide (texte, chart, table, etc.). Fourni par l'appelant
 * pour qu'on ne duplique pas `SlideContent` côté shared/ — la variante fullscreen
 * / compacte est gérée via `mode`.
 */
export type SlideContentRenderer = (props: {
  section: Section;
  mode: EditorMode;
  onEdit?: (p: Partial<Section>) => void;
}) => JSX.Element;

export type PresentationCanvasProps = {
  sections: Section[];
  mode: EditorMode;
  /** Renderer partagé du contenu d'un slide (SlideContent côté Présentation, similar côté Composition). */
  renderContent: SlideContentRenderer;
  /** Appelé après chaque réordonnancement DnD — déjà passé dans autoAdjustSpans. */
  onReorder: (next: Section[]) => void;
  onMoveUp: (i: number) => void;
  onMoveDown: (i: number) => void;
  onRegen?: (i: number) => void;
  onEdit?: (i: number, patch: Partial<Section>) => void;
  onDelete: (i: number) => void;
  onWidthChange: (i: number, w: SlideWidth) => void;
  regenIdx?: number | null;
  /** Pour Composition : le slide sélectionné est mis en surbrillance. */
  selectedIdx?: number | null;
  onSelect?: (i: number) => void;
};

/**
 * Canvas de slides partagé. Gère :
 *   - Grille 12 colonnes via CSS (.presentation-slides).
 *   - DnD sortable avec indicateur fantôme (DragOverlay).
 *   - Auto-ajustement des spans après drop.
 *   - Mode édition/présentation (classe `mode-edition` ou `mode-presentation`
 *     sur le container, lue par le CSS pour comprimer les blocs en édition).
 *   - Classe `is-drop-target` sur chaque slide quand c'est le drop target courant
 *     (permet de colorier la zone ciblée).
 */
export function PresentationCanvas(props: PresentationCanvasProps) {
  const {
    sections,
    mode,
    renderContent,
    onReorder,
    onMoveUp,
    onMoveDown,
    onRegen,
    onEdit,
    onDelete,
    onWidthChange,
    regenIdx,
    selectedIdx,
    onSelect,
  } = props;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (activeId) document.body.classList.add('dnd-dragging');
    else document.body.classList.remove('dnd-dragging');
    return () => document.body.classList.remove('dnd-dragging');
  }, [activeId]);

  const handleDragStart = (e: DragStartEvent) => {
    // eslint-disable-next-line no-console
    console.debug('[dnd] drag start', e.active?.id);
    setActiveId(String(e.active.id));
  };
  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    // eslint-disable-next-line no-console
    console.debug('[dnd] drag end', { activeId: active?.id, overId: over?.id });
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex((s) => s.id === active.id);
    const newIndex = sections.findIndex((s) => s.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    // --- Détection d'intention : latérale vs verticale ---
    // On compare le centre du bloc déplacé (translaté par le drag) au centre
    // du drop target. Si la différence verticale domine (>1.5× la différence
    // horizontale), l'utilisateur a voulu lâcher au-dessus/en-dessous =
    // nouvelle ligne. Sinon, à côté = même ligne.
    const activeRect = active.rect?.current?.translated ?? null;
    const overRect = over.rect;
    let intent: 'lateral' | 'vertical' = 'lateral';
    if (activeRect && overRect) {
      const activeCx = activeRect.left + activeRect.width / 2;
      const activeCy = activeRect.top + activeRect.height / 2;
      const overCx = overRect.left + overRect.width / 2;
      const overCy = overRect.top + overRect.height / 2;
      const dx = Math.abs(activeCx - overCx);
      const dy = Math.abs(activeCy - overCy);
      intent = dy > dx * 1.5 ? 'vertical' : 'lateral';
    }
    // eslint-disable-next-line no-console
    console.debug('[dnd] intent', intent);

    // Déplace + pose/retire le drapeau newRow sur le bloc déposé selon l'intention.
    const next = arrayMove(sections, oldIndex, newIndex).map((s) =>
      s.id === active.id ? { ...s, newRow: intent === 'vertical' } : s
    );
    onReorder(autoAdjustSpans(next));
  };

  const ids = sections.map((s) => s.id);
  const activeSection = activeId ? sections.find((s) => s.id === activeId) ?? null : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className={`presentation-slides mode-${mode}`} data-editor-mode={mode}>
          {sections.map((s, i) => (
            <SortableSlide
              key={s.id}
              section={s}
              idx={i}
              mode={mode}
              regenerating={regenIdx === i}
              selected={selectedIdx === i}
              renderContent={renderContent}
              onSelect={onSelect ? () => onSelect(i) : undefined}
              onMoveUp={() => onMoveUp(i)}
              onMoveDown={() => onMoveDown(i)}
              onRegen={onRegen ? () => onRegen(i) : undefined}
              onEdit={onEdit ? (patch) => onEdit(i, patch) : undefined}
              onDelete={() => onDelete(i)}
              onWidthChange={(w) => onWidthChange(i, w)}
            />
          ))}
        </div>
      </SortableContext>

      {/* Fantôme semi-transparent qui suit le curseur pendant le drag. */}
      <DragOverlay dropAnimation={{ duration: 180, easing: 'ease' }}>
        {activeSection ? (
          <div
            className={`presentation-slide ${spanToClass(activeSection.width)} ${activeSection.type === 'hero' ? 'slide-hero' : ''} drag-ghost`}
            data-span={String(toSpan(activeSection.width))}
          >
            {renderContent({ section: activeSection, mode })}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/* =========================================================
   SortableSlide — un bloc de la grille, draggable.
   ========================================================= */
function SortableSlide(props: {
  section: Section;
  idx: number;
  mode: EditorMode;
  regenerating: boolean;
  selected?: boolean;
  renderContent: SlideContentRenderer;
  onSelect?: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRegen?: () => void;
  onEdit?: (patch: Partial<Section>) => void;
  onDelete: () => void;
  onWidthChange: (w: SlideWidth) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({ id: props.section.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : undefined,
    zIndex: isDragging ? 10 : undefined,
  };
  const span = toSpan(props.section.width);
  const isHero = props.section.type === 'hero';

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={props.onSelect}
      className={[
        'presentation-slide',
        spanToClass(props.section.width),
        isHero ? 'slide-hero' : '',
        isDragging ? 'is-dragging' : '',
        isOver ? 'is-drop-target' : '',
        props.selected ? 'is-selected' : '',
        `type-${props.section.type}`,
      ].filter(Boolean).join(' ')}
      data-section-idx={String(props.idx + 1).padStart(2, '0')}
      data-span={String(span)}
    >
      <div
        className="slide-drag-handle"
        aria-label="Déplacer"
        title="Glisser pour réordonner"
        style={{ touchAction: 'none' }}
        {...attributes}
        {...listeners}
      >
        <IconGrip />
      </div>

      {props.regenerating && (
        <div className="card-meta" style={{ fontSize: 12 }}>
          Régénération en cours…
        </div>
      )}

      <div className="slide-inner">{props.renderContent({ section: props.section, mode: props.mode, onEdit: props.onEdit })}</div>

      <div className="slide-actions">
        {!isHero && (
          <div className="slide-width-picker" role="group" aria-label="Largeur">
            {[12, 8, 6, 4, 3].map((w) => (
              <button
                key={w}
                type="button"
                className={`swp-btn ${span === w ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); props.onWidthChange(w as SlideSpan); }}
                title={`Occuper ${w}/12 colonnes`}
                aria-pressed={span === w}
              >
                {w}
              </button>
            ))}
          </div>
        )}
        <button
          className="btn btn-ghost slide-tool-btn"
          onClick={(e) => { e.stopPropagation(); props.onMoveUp(); }}
          title="Monter"
          aria-label="Monter"
        >
          <IconArrow dir="up" />
        </button>
        <button
          className="btn btn-ghost slide-tool-btn"
          onClick={(e) => { e.stopPropagation(); props.onMoveDown(); }}
          title="Descendre"
          aria-label="Descendre"
        >
          <IconArrow dir="down" />
        </button>
        {props.onRegen && (
          <button
            className="btn btn-ghost slide-tool-btn"
            onClick={(e) => { e.stopPropagation(); props.onRegen!(); }}
            title="Régénérer avec l'IA"
            aria-label="Régénérer"
          >
            <IconSparkle />
          </button>
        )}
        <button
          className="btn btn-ghost slide-tool-btn"
          onClick={(e) => { e.stopPropagation(); props.onDelete(); }}
          title="Supprimer"
          aria-label="Supprimer"
        >
          <IconTrash />
        </button>
      </div>
    </div>
  );
}

/* =========================================================
   AutoSave — badge "Enregistrement… / ✓ Enregistré".
   ========================================================= */
export function AutoSaveBadge({ status }: { status: 'idle' | 'saving' | 'saved' }) {
  if (status === 'idle') return null;
  return (
    <span className="autosave-indicator" data-state={status} role="status" aria-live="polite">
      {status === 'saving' ? 'Enregistrement…' : 'Enregistré'}
    </span>
  );
}

/* =========================================================
   PresentationWrap — applique le thème + orientation + custom vars.
   Utilisé par les deux onglets pour wrap le canvas.
   ========================================================= */
export function PresentationWrap({
  themeClassName,
  orientation,
  customPreview,
  customStyle,
  children,
  innerRef,
}: {
  themeClassName: string;
  orientation: 'portrait' | 'landscape';
  customPreview?: boolean;
  customStyle?: React.CSSProperties;
  children: ReactNode;
  innerRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      id="presentation-export-target"
      ref={innerRef}
      className={`pres-theme-wrap ${themeClassName}`}
      data-orientation={orientation}
      data-custom-preview={customPreview ? '1' : undefined}
      style={customStyle}
    >
      {children}
    </div>
  );
}
